import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Slider } from "@/components/ui/slider";
import { TrendingDown } from "lucide-react";
import { CS } from "@/constants/testIds/clickshield";

const PLATFORMS = [
  { id: "google", label: "Google Ads", rate: 0.18 },
  { id: "meta", label: "Meta Ads", rate: 0.14 },
  { id: "bing", label: "Bing Ads", rate: 0.11 },
];

export const SavingsCalculator = () => {
  const [spend, setSpend] = useState([12000]);
  const [platform, setPlatform] = useState(PLATFORMS[0]);

  const monthly = spend[0];
  const savings = useMemo(() => Math.round(monthly * platform.rate), [monthly, platform]);
  const yearly = savings * 12;
  const fraudPct = Math.round(platform.rate * 100);

  return (
    <section id="calculator" className="relative py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="font-mono text-xs tracking-widest uppercase text-cyan-neon">[01] ROI Calculator</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-4 leading-tight">
              See exactly how much you're losing to fraud.
            </h2>
            <p className="mt-5 text-slate-300 font-light leading-relaxed">
              Between 14% and 30% of paid ad clicks are fraudulent. Drag the slider to your monthly
              spend and watch what ClickShield AI puts back in your pocket.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-[#39FF14]/5 border border-[#39FF14]/20">
              <TrendingDown className="w-5 h-5 text-[#39FF14]" />
              <span className="text-sm text-slate-200">
                Avg. customer recovers <span className="text-[#39FF14] font-semibold">{fraudPct}%</span> of {platform.label} spend
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="rounded-2xl neon-border bg-[#0B101A]/80 backdrop-blur-xl p-8 lg:p-10">
              <div className="flex items-center justify-between mb-3">
                <label className="font-mono text-xs tracking-widest uppercase text-slate-400">Monthly ad spend</label>
                <span className="font-display text-2xl text-cyan-neon tabular-nums">${monthly.toLocaleString()}</span>
              </div>
              <Slider
                data-testid={CS.calcSpendSlider}
                value={spend}
                onValueChange={setSpend}
                min={1000}
                max={100000}
                step={1000}
                className="my-4"
              />
              <div className="flex justify-between font-mono text-[10px] tracking-wider text-slate-500">
                <span>$1K</span>
                <span>$100K+</span>
              </div>

              <div className="mt-8">
                <label className="font-mono text-xs tracking-widest uppercase text-slate-400">Primary platform</label>
                <div data-testid={CS.calcPlatform} className="mt-3 grid grid-cols-3 gap-2">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`calc-platform-${p.id}`}
                      onClick={() => setPlatform(p)}
                      className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors border ${
                        platform.id === p.id
                          ? "bg-cyan-neon/10 border-cyan-neon/50 text-cyan-neon"
                          : "border-white/10 text-slate-300 hover:border-white/25"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-9 pt-8 border-t border-white/10 grid sm:grid-cols-2 gap-6">
                <div data-testid={CS.calcSavings}>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Est. monthly savings</div>
                  <div className="font-display text-4xl text-[#39FF14] text-glow-cyan mt-2 tabular-nums">
                    $<CountUp end={savings} separator="," duration={0.6} preserveValue />
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Recovered per year</div>
                  <div className="font-display text-4xl text-white mt-2 tabular-nums">
                    $<CountUp end={yearly} separator="," duration={0.6} preserveValue />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
